import React, { useEffect, useState } from 'react';
import { Modal } from '@mantine/core';
import toast from 'react-hot-toast';

import SearchInput from '@components/shared/SearchInput';
import { retrieveExercises } from '@util/athleteSession';
import { toErrorMessage } from '@util/toErrorMessage';

import classes from './AddExerciseSheet.module.css';

function formatRestLabel(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  if (safe === 0) return '0s';
  if (safe < 60) return `${safe}s`;
  const minutes = Math.floor(safe / 60);
  const rem = safe % 60;
  return rem ? `${minutes}m ${rem}s` : `${minutes}m`;
}

export default function AddExerciseSheet({
  opened,
  onClose,
  onConfirm,
  confirming = false,
}) {
  const [name, setName] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [sets, setSets] = useState(3);
  const [reps, setReps] = useState(8);
  const [restSeconds, setRestSeconds] = useState(90);

  useEffect(() => {
    if (!opened) return undefined;
    setName('');
    setResults([]);
    setSelected(null);
    setSets(3);
    setReps(8);
    setRestSeconds(90);
    return undefined;
  }, [opened]);

  useEffect(() => {
    if (!opened) return undefined;
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setSearching(true);
      retrieveExercises({ name: name.trim(), page: 0, pageSize: 25 })
        .then((payload) => {
          if (cancelled) return;
          setResults(payload.data || []);
        })
        .catch((error) => {
          if (cancelled) return;
          toast.error(toErrorMessage(error));
          setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [opened, name]);

  function bump(setter, delta, min = 1) {
    setter((current) => Math.max(min, Number(current || 0) + delta));
  }

  async function onSubmit() {
    if (!selected?.id || confirming) return;
    if (sets < 1 || reps < 1) {
      toast.error('Sets and reps must be at least 1');
      return;
    }
    if (restSeconds < 0) {
      toast.error('Rest cannot be negative');
      return;
    }
    await onConfirm({
      exerciseId: selected.id,
      exerciseName: selected.name,
      sets,
      reps,
      restSeconds,
    });
  }

  return (
    <Modal
      opened={opened}
      onClose={confirming ? undefined : onClose}
      title="Add exercise"
      centered
      size="lg"
    >
      <div className={classes.body}>
        <SearchInput
          value={name}
          onChange={setName}
          placeholder="Search exercises…"
        />
        <div className={classes.results}>
          {searching ? (
            <p className={classes.hint}>Searching…</p>
          ) : results.length ? (
            <ul className={classes.list}>
              {results.map((exercise) => {
                const active = selected?.id === exercise.id;
                return (
                  <li key={exercise.id}>
                    <button
                      type="button"
                      className={[
                        classes.resultBtn,
                        active ? classes.resultBtnActive : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => setSelected(exercise)}
                      disabled={confirming}
                    >
                      {exercise.name}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className={classes.hint}>
              {name.trim()
                ? 'No exercises match that name.'
                : 'Type to search the exercise library.'}
            </p>
          )}
        </div>

        {selected ? (
          <div className={classes.prescription}>
            <p className={classes.selectedName}>{selected.name}</p>
            <div className={classes.stepperRow}>
              <div className={classes.stepper}>
                <span className={classes.stepperLabel}>Sets</span>
                <div className={classes.stepperControls}>
                  <button
                    type="button"
                    className={classes.stepperBtn}
                    onClick={() => bump(setSets, -1)}
                    disabled={confirming || sets <= 1}
                  >
                    −
                  </button>
                  <span className={classes.stepperValue}>{sets}</span>
                  <button
                    type="button"
                    className={classes.stepperBtn}
                    onClick={() => bump(setSets, 1)}
                    disabled={confirming}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className={classes.stepper}>
                <span className={classes.stepperLabel}>Reps</span>
                <div className={classes.stepperControls}>
                  <button
                    type="button"
                    className={classes.stepperBtn}
                    onClick={() => bump(setReps, -1)}
                    disabled={confirming || reps <= 1}
                  >
                    −
                  </button>
                  <span className={classes.stepperValue}>{reps}</span>
                  <button
                    type="button"
                    className={classes.stepperBtn}
                    onClick={() => bump(setReps, 1)}
                    disabled={confirming}
                  >
                    +
                  </button>
                </div>
              </div>
              <div className={classes.stepper}>
                <span className={classes.stepperLabel}>Rest</span>
                <div className={classes.stepperControls}>
                  <button
                    type="button"
                    className={classes.stepperBtn}
                    onClick={() => bump(setRestSeconds, -15, 0)}
                    disabled={confirming || restSeconds <= 0}
                  >
                    −
                  </button>
                  <span className={classes.stepperValue}>
                    {formatRestLabel(restSeconds)}
                  </span>
                  <button
                    type="button"
                    className={classes.stepperBtn}
                    onClick={() => bump(setRestSeconds, 15, 0)}
                    disabled={confirming}
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <div className={classes.actions}>
          <button
            type="button"
            className={classes.secondaryBtn}
            onClick={onClose}
            disabled={confirming}
          >
            Cancel
          </button>
          <button
            type="button"
            className={classes.primaryBtn}
            onClick={onSubmit}
            disabled={!selected || confirming}
          >
            {confirming ? 'Adding…' : 'Add to workout'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
